import api from '@/core/api/client';

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  role: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateStaffUserData {
  name: string;
  email: string;
  password: string;
  role: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
}

export interface UpdateStaffUserData {
  name?: string;
  email?: string;
  password?: string;
  role?: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
  isActive?: boolean;
}

export const usersService = {
  async list(params: PaginationParams = {}): Promise<PaginatedResponse<StaffUser>> {
    const response = await api.get('/users', { params });
    if (response.data.success) {
      return {
        data: response.data.data,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.data.message || 'Erro ao listar usuários');
  },

  async get(id: string): Promise<StaffUser> {
    const response = await api.get(`/users/${id}`);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao buscar usuário');
  },

  async create(data: CreateStaffUserData): Promise<StaffUser> {
    const response = await api.post('/users', data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao criar usuário');
  },

  async update(id: string, data: UpdateStaffUserData): Promise<StaffUser> {
    const response = await api.put(`/users/${id}`, data);
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao atualizar usuário');
  },

  async delete(id: string): Promise<void> {
    const response = await api.delete(`/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao deletar usuário');
    }
  },

  async anonymize(id: string): Promise<void> {
    const response = await api.post(`/users/${id}/anonymize`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao excluir permanentemente usuário');
    }
  },

  async toggleActive(id: string, isActive: boolean): Promise<StaffUser> {
    const response = await api.patch(`/users/${id}/toggle-active`, { isActive });
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao alterar status do usuário');
  },
};
